import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import * as Yup from "yup";
import { BRAND_BLUE } from "../../shared/pricingHelpers";

export interface FeatureCatalogValues {
  feature_key: string;
  display_name: string;
  feature_group: string;
  display_price_usd: number | "";
  display_price_inr: number | "";
  credit_charge: number | "";
  calculator_visible: boolean;
  display_order: number | "";
  is_active: boolean;
  // Phase-2 deduction routing (advanced)
  msc_feature_id: number | "";
  msc_action_event: string;
  msc_content_type_id: number | "";
  msc_external_service_resource_id: number | "";
  msc_unit_type: string;
  msc_charge_status: string;
}

const EMPTY: FeatureCatalogValues = {
  feature_key: "",
  display_name: "",
  feature_group: "",
  display_price_usd: "",
  display_price_inr: "",
  credit_charge: "",
  calculator_visible: true,
  display_order: 0,
  is_active: true,
  msc_feature_id: "",
  msc_action_event: "",
  msc_content_type_id: "",
  msc_external_service_resource_id: "",
  msc_unit_type: "ACTION",
  msc_charge_status: "ACTIVE",
};

interface Props {
  open: boolean;
  mode: "create" | "edit";
  initial?: Partial<FeatureCatalogValues> | null;
  saving?: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => void;
}

const schema = Yup.object().shape({
  feature_key: Yup.string().trim().required("Feature key is required"),
  display_name: Yup.string().trim().required("Display name is required"),
  feature_group: Yup.string().trim().required("Feature group is required"),
  display_price_usd: Yup.number()
    .typeError("USD price is required")
    .required("USD price is required")
    .min(0, "Must be ≥ 0"),
  credit_charge: Yup.number()
    .typeError("Credit charge is required")
    .required("Credit charge is required")
    .min(0, "Must be ≥ 0"),
});

// Strip "" → null for optional fields; coerce numerics. Required numerics are validated.
const buildPayload = (v: FeatureCatalogValues) => {
  const num = (x: number | "") => (x === "" ? null : Number(x));
  const str = (x: string) => (x.trim() === "" ? null : x.trim());
  return {
    feature_key: v.feature_key.trim(),
    display_name: v.display_name.trim(),
    feature_group: v.feature_group.trim(),
    display_price_usd: Number(v.display_price_usd),
    display_price_inr: num(v.display_price_inr),
    credit_charge: Number(v.credit_charge),
    calculator_visible: v.calculator_visible,
    display_order: v.display_order === "" ? 0 : Number(v.display_order),
    is_active: v.is_active,
    msc_feature_id: num(v.msc_feature_id),
    msc_action_event: str(v.msc_action_event),
    msc_content_type_id: num(v.msc_content_type_id),
    msc_external_service_resource_id: num(v.msc_external_service_resource_id),
    msc_unit_type: v.msc_unit_type,
    msc_charge_status: v.msc_charge_status,
  };
};

const FeatureCatalogForm: React.FC<Props> = ({
  open,
  mode,
  initial,
  saving,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] = useState<FeatureCatalogValues>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setValues({ ...EMPTY, ...(initial || {}) } as FeatureCatalogValues);
      setErrors({});
    }
  }, [open, initial]);

  const set = (name: keyof FeatureCatalogValues, value: any) =>
    setValues((p) => ({ ...p, [name]: value }));

  const handleSubmit = async () => {
    try {
      await schema.validate(values, { abortEarly: false });
      setErrors({});
      onSubmit(buildPayload(values));
    } catch (err: any) {
      const e: Record<string, string> = {};
      err.inner?.forEach((x: any) => (e[x.path] = x.message));
      setErrors(e);
    }
  };

  const grid2 = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: BRAND_BLUE }}>
        {mode === "create" ? "Add Feature" : "Edit Feature"}
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ mt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Feature Key"
            value={values.feature_key}
            onChange={(e) => set("feature_key", e.target.value)}
            error={Boolean(errors.feature_key)}
            helperText={errors.feature_key || "Stable id, e.g. ai_interview_deep"}
            size="small"
            fullWidth
          />
          <TextField
            label="Display Name"
            value={values.display_name}
            onChange={(e) => set("display_name", e.target.value)}
            error={Boolean(errors.display_name)}
            helperText={errors.display_name}
            size="small"
            fullWidth
          />
          <TextField
            label="Feature Group"
            value={values.feature_group}
            onChange={(e) => set("feature_group", e.target.value)}
            error={Boolean(errors.feature_group)}
            helperText={errors.feature_group || "e.g. AI Interviews"}
            size="small"
            fullWidth
          />
          <Box sx={grid2}>
            <TextField
              label="Display Price (USD)"
              type="number"
              value={values.display_price_usd}
              onChange={(e) =>
                set("display_price_usd", e.target.value === "" ? "" : Number(e.target.value))
              }
              error={Boolean(errors.display_price_usd)}
              helperText={errors.display_price_usd}
              size="small"
            />
            <TextField
              label="Display Price (INR)"
              type="number"
              value={values.display_price_inr}
              onChange={(e) =>
                set("display_price_inr", e.target.value === "" ? "" : Number(e.target.value))
              }
              helperText="Optional"
              size="small"
            />
          </Box>
          <Box sx={grid2}>
            <TextField
              label="Credit Charge"
              type="number"
              value={values.credit_charge}
              onChange={(e) =>
                set("credit_charge", e.target.value === "" ? "" : Number(e.target.value))
              }
              error={Boolean(errors.credit_charge)}
              helperText={errors.credit_charge}
              size="small"
            />
            <TextField
              label="Display Order"
              type="number"
              value={values.display_order}
              onChange={(e) =>
                set("display_order", e.target.value === "" ? "" : Number(e.target.value))
              }
              size="small"
            />
          </Box>
          <Box sx={{ display: "flex", gap: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={values.calculator_visible}
                  onChange={(e) => set("calculator_visible", e.target.checked)}
                  color="primary"
                />
              }
              label="Calculator visible"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={values.is_active}
                  onChange={(e) => set("is_active", e.target.checked)}
                  color="primary"
                />
              }
              label="Active"
            />
          </Box>

          <Accordion
            disableGutters
            elevation={0}
            sx={{ border: "1px solid #e6e9f0", borderRadius: 1, "&:before": { display: "none" } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#475467" }}>
                Advanced — Phase-2 deduction routing
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={grid2}>
                  <TextField
                    label="MSC Feature ID"
                    type="number"
                    value={values.msc_feature_id}
                    onChange={(e) =>
                      set("msc_feature_id", e.target.value === "" ? "" : Number(e.target.value))
                    }
                    size="small"
                  />
                  <TextField
                    label="MSC Action Event"
                    value={values.msc_action_event}
                    onChange={(e) => set("msc_action_event", e.target.value)}
                    size="small"
                  />
                </Box>
                <Box sx={grid2}>
                  <TextField
                    label="MSC Content Type ID"
                    type="number"
                    value={values.msc_content_type_id}
                    onChange={(e) =>
                      set("msc_content_type_id", e.target.value === "" ? "" : Number(e.target.value))
                    }
                    size="small"
                  />
                  <TextField
                    label="MSC External Resource ID"
                    type="number"
                    value={values.msc_external_service_resource_id}
                    onChange={(e) =>
                      set(
                        "msc_external_service_resource_id",
                        e.target.value === "" ? "" : Number(e.target.value)
                      )
                    }
                    size="small"
                  />
                </Box>
                <Box sx={grid2}>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="msc-unit-type">Unit Type</InputLabel>
                    <Select
                      labelId="msc-unit-type"
                      label="Unit Type"
                      value={values.msc_unit_type}
                      onChange={(e) => set("msc_unit_type", e.target.value)}
                    >
                      <MenuItem value="ACTION">ACTION</MenuItem>
                      <MenuItem value="TIME">TIME</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl size="small" fullWidth>
                    <InputLabel id="msc-charge-status">Charge Status</InputLabel>
                    <Select
                      labelId="msc-charge-status"
                      label="Charge Status"
                      value={values.msc_charge_status}
                      onChange={(e) => set("msc_charge_status", e.target.value)}
                    >
                      <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                      <MenuItem value="UPCOMING">UPCOMING</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: BRAND_BLUE,
            "&:hover": { backgroundColor: "#0445a3" },
          }}
        >
          {mode === "create" ? "Create" : "Save changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FeatureCatalogForm;
